import { AIGC_TAG_GROUPS, type ModelType } from '@/pages/data-center/artifact-center/components/OpsPublishModal/constants';

/** 筛选芯片色调：每个叶子类别一组色，同类别下 tag 同色 */

export type FilterChipTone =
  | 'primary'
  | 'rose'
  | 'violet'
  | 'orange'
  | 'sky'
  | 'green'
  | 'teal'
  | 'pink'
  | 'amber'
  | 'gold'
  | 'cyan'
  | 'indigo'
  | 'lime'
  | 'fuchsia'
  | 'slate'
  | 'blue';

/** 产品列表页「产品」筛选项 / SKU 卡片所属产品标签共用色 */
export const PRODUCT_FILTER_TONE: FilterChipTone = 'gold';

/** 热门标签 */
export const HOT_TAG_TONE: FilterChipTone = 'orange';

/** 技能分类卡片标签：主题色浅色 */
export const SKILL_CATEGORY_TONE: FilterChipTone = 'primary';

/** 厂商 */
export const VENDOR_TONE: FilterChipTone = 'slate';

/** AIGC 模型区块默认色 */
export const AIGC_SECTION_TONE: FilterChipTone = 'pink';

/** 科学模型区块默认色 */
export const SCIENCE_SECTION_TONE: FilterChipTone = 'teal';

/** 未识别固定标签时的回退色 */
export const OPS_TAG_FALLBACK_TONE: FilterChipTone = 'violet';

/** 叶子类别轮换色板（无显式 map 时按类别顺序取色） */
export const CATEGORY_TONE_CYCLE: FilterChipTone[] = [
  'violet',
  'orange',
  'sky',
  'green',
  'teal',
  'pink',
  'amber',
  'cyan',
  'indigo',
  'lime',
  'blue',
  'fuchsia',
];

/** Skill 广场分类色调 */
export const SKILL_TYPE_TONE_MAP: Record<string, FilterChipTone> = {
  Skills: 'violet',
  DevTools: 'blue',
  Marketing: 'orange',
  Frontend: 'sky',
  MediaAI: 'pink',
  Quality: 'indigo',
  Mobile: 'cyan',
  CloudOps: 'teal',
  Other: 'slate',
};

/** BASE 一级任务分类色调（二级叶子任务时，整组 tag 同色） */
export const BASE_TASK_TONE_MAP: Record<string, FilterChipTone> = {
  'multimodal.tasks': 'violet',
  'natural.language.processing': 'orange',
  speech: 'sky',
  'computer.vision': 'green',
  'scientific.computing': 'teal',
};

/**
 * computer.vision 下最底层「叶子类别」色调（二级类目）。
 * 该类目下的三级 tag 芯片共用此色，不按单个 tag 拆色。
 */
export const COMPUTER_VISION_L2_TONE_MAP: Record<string, FilterChipTone> = {
  'visual.detection.and.tracking': 'green',
  'optical.character.recognition': 'teal',
  'face.and.body': 'pink',
  'vision.classification': 'amber',
  'vision.editing': 'orange',
  'vision.segmentation': 'sky',
  'vision.generation': 'violet',
  'vision.representation': 'indigo',
  'vision.evaluation': 'cyan',
  'low.level.vision': 'lime',
  '3d.vision': 'blue',
  'foundation.model.applications': 'fuchsia',
};

/** AIGC 叶子类别（设计风格 / 模型功能 / 内容元素）色调 */
export const AIGC_GROUP_TONE_MAP: Record<string, FilterChipTone> = {
  designStyle: 'pink',
  modelFunction: 'amber',
  contentElement: 'cyan',
};

/** 科学学科叶子类别（一级目录）色调 */
export const SCIENCE_CATALOGUE_TONE_MAP: Record<string, FilterChipTone> = {
  'life.sciences': 'lime',
  'materials.science': 'amber',
  'earth.science': 'sky',
  'social.sciences': 'orange',
  'general.discipline': 'indigo',
  physics: 'violet',
};

/** 按类别 key 取色，未命中则按 cycle */
export function resolveCategoryTone(
  categoryKey: string | undefined,
  index = 0,
  fallback: FilterChipTone = OPS_TAG_FALLBACK_TONE,
): FilterChipTone {
  if (!categoryKey) return fallback;
  return (
    COMPUTER_VISION_L2_TONE_MAP[categoryKey] ??
    BASE_TASK_TONE_MAP[categoryKey] ??
    SCIENCE_CATALOGUE_TONE_MAP[categoryKey] ??
    AIGC_GROUP_TONE_MAP[categoryKey] ??
    CATEGORY_TONE_CYCLE[index % CATEGORY_TONE_CYCLE.length] ??
    fallback
  );
}

/** AIGC 叶子 value → 所属分组色调 */
let aigcValueToneMap: Record<string, FilterChipTone> | null = null;

function getAigcValueToneMap(): Record<string, FilterChipTone> {
  if (aigcValueToneMap) return aigcValueToneMap;
  const map: Record<string, FilterChipTone> = {};
  for (const group of AIGC_TAG_GROUPS) {
    const tone = AIGC_GROUP_TONE_MAP[group.key] ?? AIGC_SECTION_TONE;
    for (const option of group.options) {
      map[option.value] = tone;
    }
  }
  aigcValueToneMap = map;
  return map;
}

/**
 * 固定标签色调：按叶子类别着色（同类别下多个 tag 同色）。
 * - computer.vision/二级/三级 → 取二级类别色
 * - 一级/二级 → 取一级类别色
 * - AIGC 扁平 value → 取所属分组色
 */
export function resolveOpsTagTone(opsTagValue: string, modelType?: ModelType | string): FilterChipTone {
  if (!opsTagValue) return OPS_TAG_FALLBACK_TONE;

  const skillTone = SKILL_TYPE_TONE_MAP[opsTagValue];
  if (skillTone) return skillTone;

  if (opsTagValue.includes('/')) {
    const parts = opsTagValue.split('/');
    const root = parts[0];
    const second = parts[1];

    // 三层路径：叶子类别为二级
    if (root === 'computer.vision' && second) {
      return COMPUTER_VISION_L2_TONE_MAP[second] ?? BASE_TASK_TONE_MAP[root] ?? OPS_TAG_FALLBACK_TONE;
    }

    if (modelType === 'SCIENCE' || root in SCIENCE_CATALOGUE_TONE_MAP) {
      return SCIENCE_CATALOGUE_TONE_MAP[root] ?? SCIENCE_SECTION_TONE;
    }
    return BASE_TASK_TONE_MAP[root] ?? OPS_TAG_FALLBACK_TONE;
  }

  const aigcTone = getAigcValueToneMap()[opsTagValue];
  if (aigcTone) return aigcTone;

  if (modelType === 'AIGC') return AIGC_SECTION_TONE;
  if (modelType === 'SCIENCE') return SCIENCE_SECTION_TONE;

  return OPS_TAG_FALLBACK_TONE;
}

/** Skill 分类卡片标签统一主题浅色（不再按分类拆多色） */
export function resolveSkillTypeTone(_skillTypeValue: string): FilterChipTone {
  return SKILL_CATEGORY_TONE;
}
